import { debug } from '@exp/exp-utils/helper/logger';

import { getWindow, getDocument, formatPageName } from '@exp/exp-utils/helper/browser';
import { getCookieValues, setCookie } from '@exp/exp-utils/helper/cookie';
import { getCleanQueryString } from '@exp/exp-utils/helper/url';
import { merge } from '@exp/exp-utils/helper/object';
import SchemaHandler from '@exp/exp-utils/schema/schemaHandler';

import config from '@exp/exp-utils/helper/config';
import page from '@tcc/shared/src/interfaces/v1/eventPageProperties';

import { sendEvent } from '@tcc/shared/src/integrations/eventService/eventSend';
import { createEventProperties, getEventSvcProperties, createTData } from '../../../interfaces/v1/eventProperties';
import { sendPageView as sendGaPageView } from '../../../integrations/ga/ga';
import { getTraceId } from '@tcc/shared/src/helpers/page';
import { createGuid } from '@tcc/shared/src/helpers/guid';

let lastPathName;

class AddPageRequestHandler extends SchemaHandler {
  process() {
    super.process({
      EVENT_SVC: () => { this._handleEventSvc(); },
      GA: (input) => { this._handleGA(input); }
    });
  }

  _handleEventSvc() {
    sendEvent(
      merge(page.getProperties(), getEventSvcProperties()),
      '/image.aspx',
      'GET');
  }

  _handleGA(input) {
    // Send 'Page View' record to Google Analytics
    sendGaPageView(input.virtual_path, merge(this.tData.getProperties(), page.getProperties(), this.extras));
  }

  _getViewProperties() {
    const v = {};
    const win = getWindow();
    const doc = getDocument();

    v.browx = win.innerWidth ? win.innerWidth : doc.body.offsetWidth;
    v.browy = win.innerHeight ? win.innerHeight : doc.body.offsetHeight;
    v.resx = win.screen.width;
    v.resy = win.screen.height;
    v.cdepth = win.screen.colorDepth;

    return v;
  }

  preProcess() {
    // Build tData object
    this.tData = createTData(this.data);

    let pathname = this.data.ALL.virtual_path;

    if (typeof (pathname) === 'undefined') {
      // Send just the pathname to Traffic unless a virtual page was specified
      pathname = getWindow().location.pathname;
    } else {
      page.set('virtual_path', pathname);
    }

    if (process.env.NODE_ENV !== 'production') {
      debug('cmd: LogPageRequest invoked with path', pathname);
    }

    if (lastPathName === pathname) {
      throw 'cmd: LogPageRequest duplicate page request detected, ignoring duplicate path';
    }
    lastPathName = pathname;

    // Add generic event properties
    page.merge(createEventProperties('page.request', this.tData));

    page.set('page', formatPageName(pathname));
    page.set('hw', getWindow().history.length);

    // merge properties from browser view stats
    page.merge(this._getViewProperties());

    // get query string from URL
    const qs = getCleanQueryString();
    if (qs) {
      page.set('querystring', qs);
    }

    // Only set traceId if there is a value. This will allow for
    // SPAs to reuse the hard page load's traceId until the traceId
    // is updated on virtual route changes.
    let traceId = page.get('trace_id');
    if (!traceId) {
      traceId = getTraceId();
      if (traceId && !config.get('tcc.disablePageTracing')) {
        page.set('trace_id', traceId);
        page.set('traced', 1);
      } else {
        // Only generate a GUID if one has not been previously set.
        // This means that the TraceID will not update for SPAs unless the DOM has been updated.

        // generate trace id with guid
        page.set('trace_id', createGuid().replace(/-/g, ''));
      }
    }

    page.merge(getCookieValues('tcc_refer'));
    setCookie('tcc_refer', '', -1, '/');

    // Merge datalayer extras, which includes an identifier to
    // correlate the hit between downstream systems (hit_id)
    page.merge(this.extras);
  }
}

export default AddPageRequestHandler;
